/**
 * Website Analytics Utilities for JustFun
 * Tracks user interactions and conversions from website to app
 */

export interface WebsiteAnalytics {
  trackDownloadClick: (source: string, medium: string, campaign: string) => void;
  trackPageView: (page: string) => void;
  trackGamePlay: (gameName: string) => void;
  trackConversion: (conversionType: string, value?: number) => void;
}

class WebsiteAnalyticsImpl implements WebsiteAnalytics {
  
  /**
   * Track download button clicks with detailed attribution
   */
  trackDownloadClick(source: string, medium: string, campaign: string) {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'download_click', {
        event_category: 'conversion',
        event_label: `${source}_${medium}`,
        custom_parameters: {
          utm_source: source,
          utm_medium: medium,
          utm_campaign: campaign
        },
        value: 1
      });
    }
    
    // Also track in custom analytics
    this.trackCustomEvent('website_download_click', {
      source,
      medium,
      campaign,
      timestamp: Date.now()
    });
  }
  
  /**
   * Track page views for website analytics
   */
  trackPageView(page: string) {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('config', 'GA_MEASUREMENT_ID', {
        page_title: page,
        page_location: window.location.href
      });
    }
  }
  
  /**
   * Track game plays on website
   */
  trackGamePlay(gameName: string) {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'game_play', {
        event_category: 'engagement',
        event_label: gameName,
        value: 1
      });
    }
  }
  
  /**
   * Track conversions (downloads, signups, etc.)
   */
  trackConversion(conversionType: string, value: number = 1) {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'conversion', {
        event_category: 'conversion',
        event_label: conversionType,
        value: value
      });
    }
  }
  
  /**
   * Track custom events
   */
  private trackCustomEvent(eventName: string, parameters: Record<string, any>) {
    // Store in localStorage for debugging
    const events = JSON.parse(localStorage.getItem('justfun_analytics') || '[]');
    events.push({
      event: eventName,
      parameters,
      timestamp: new Date().toISOString()
    });
    localStorage.setItem('justfun_analytics', JSON.stringify(events));
  }
  
  /**
   * Get analytics data for debugging
   */
  getAnalyticsData() {
    return JSON.parse(localStorage.getItem('justfun_analytics') || '[]');
  }
}

// Export singleton instance
export const websiteAnalytics = new WebsiteAnalyticsImpl();

/**
 * Create UTM-tracked Play Store URLs
 */
export const createTrackedPlayStoreUrl = (
  source: string,
  medium: string,
  campaign: string,
  content?: string,
  term?: string
): string => {
  const baseUrl = 'https://play.google.com/store/apps/details?id=com.sssk.justfun';
  const utmParams = new URLSearchParams({
    'utm_source': source,
    'utm_medium': medium,
    'utm_campaign': campaign,
    ...(content && { 'utm_content': content }),
    ...(term && { 'utm_term': term })
  });
  
  return `${baseUrl}&${utmParams.toString()}`;
};

/**
 * Predefined tracking configurations
 */
export const TRACKING_CONFIGS = {
  WEBSITE_HOMEPAGE: {
    source: 'website',
    medium: 'homepage',
    campaign: 'website_conversion',
    content: 'cta_button',
    term: 'justfun_homepage'
  },
  WEBSITE_BANNER: {
    source: 'website',
    medium: 'download_banner',
    campaign: 'website_conversion',
    content: 'banner_download',
    term: 'justfun_website'
  },
  QR_CODE: {
    source: 'qr_code',
    medium: 'offline',
    campaign: 'qr_conversion',
    content: 'qr_scan',
    term: 'justfun_qr'
  },
  SOCIAL_MEDIA: (platform: string) => ({
    source: platform,
    medium: 'social',
    campaign: 'social_conversion',
    content: 'social_post',
    term: `justfun_${platform}`
  })
};
